import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, integer, record, text, textArray } from "../shared/validators";

export interface QuizQuestion {
  readonly prompt: string;
  readonly answers: readonly [string, string, string, string];
  readonly correctIndex: number;
}

export interface QuizSpec {
  readonly version: 1;
  readonly kind: "quiz";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly params: {
    readonly questions: readonly QuizQuestion[];
    readonly secondsPerQuestion: number;
    readonly goalCorrect: number;
    /** 1 = solo (default); 2 = local pass-and-play, players alternate questions. */
    readonly players?: number;
  };
}

function parseQuestion(value: unknown, path: string): QuizQuestion {
  const question = record(value, path);
  exactKeys(question, ["prompt", "answers", "correctIndex"], path);
  const answers = textArray(question.answers, `${path}.answers`, 4, 1, 60);
  const distinct = new Set(answers.map((answer) => answer.trim().toLowerCase()));
  if (distinct.size !== 4) throw new GameSpecValidationError(`${path}.answers must be four distinct options`);
  return {
    prompt: text(question.prompt, `${path}.prompt`, 1, 140),
    answers: answers as [string, string, string, string],
    correctIndex: integer(question.correctIndex, `${path}.correctIndex`, 0, 3),
  };
}

/** A trivia round: the model authors question/answer data, never code -- rendered as plain text, never markup. */
export function parseQuizSpec(root: Record<string, unknown>): QuizSpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "params"], "game spec");
  if (root.kind !== "quiz") throw new GameSpecValidationError("unsupported game kind");

  const params = record(root.params, "params");
  boundedKeys(params, ["questions", "secondsPerQuestion", "goalCorrect"], ["players"], "params");

  if (!Array.isArray(params.questions) || params.questions.length < 3 || params.questions.length > 15) {
    throw new GameSpecValidationError("params.questions must contain 3-15 entries");
  }
  const questions = params.questions.map((question, index) => parseQuestion(question, `params.questions[${index}]`));
  const secondsPerQuestion = integer(params.secondsPerQuestion, "params.secondsPerQuestion", 5, 30);
  for (const [index, question] of questions.entries()) {
    const readingFloor = Math.max(5, Math.ceil((question.prompt.length + Math.max(...question.answers.map((a) => a.length))) / 12));
    if (secondsPerQuestion < readingFloor) {
      throw new GameSpecValidationError(`params.secondsPerQuestion is too short to read params.questions[${index}]`);
    }
  }
  const goalCorrect = integer(params.goalCorrect, "params.goalCorrect", 1, questions.length);
  const players = params.players === undefined ? undefined : integer(params.players, "params.players", 1, 2);

  return {
    version: 1,
    kind: "quiz",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    params: { questions, secondsPerQuestion, goalCorrect, ...(players === undefined ? {} : { players }) },
  };
}
